/**
 * In-app sound effects library + Text-to-Speech helper for the Creator Studio.
 * No external audio assets needed — Web Audio API synthesis + browser TTS.
 */

let ctx = null;
function getCtx() {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    ctx = AC ? new AC() : null;
  } catch { ctx = null; }
  return ctx;
}

// Re-export from giftSounds for convenience (so Creator Studio can preview gift SFX too)
export { playGiftSound } from "./giftSounds";

const SFX_CATALOG = [
  { id: "applause",   label: "Applause",      emoji: "👏" },
  { id: "airhorn",    label: "Airhorn",       emoji: "📣" },
  { id: "drumroll",   label: "Drumroll",      emoji: "🥁" },
  { id: "ding",       label: "Ding",          emoji: "🔔" },
  { id: "boom",       label: "Boom",          emoji: "💥" },
  { id: "swoosh",     label: "Swoosh",        emoji: "💨" },
  { id: "record_scratch", label: "Scratch",   emoji: "💿" },
  { id: "cash",       label: "Cha-Ching",     emoji: "💰" },
  { id: "laser",      label: "Laser",         emoji: "🔫" },
  { id: "pop",        label: "Pop",           emoji: "🎈" },
  { id: "magic",      label: "Magic Sparkle", emoji: "🪄" },
  { id: "siren",      label: "Siren",         emoji: "🚨" },
];

export function listSoundEffects() {
  return SFX_CATALOG;
}

function tone(freq, t0, duration, type = "sine", peak = 0.3) {
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(gain).connect(c.destination);
  osc.start(t0); osc.stop(t0 + duration + 0.05);
}

function noiseBurst(t0, duration, peak = 0.15, filterFreq = 1200) {
  const c = getCtx();
  if (!c) return;
  const buf = c.createBuffer(1, Math.max(c.sampleRate * duration, 1), c.sampleRate);
  const out = buf.getChannelData(0);
  for (let i = 0; i < out.length; i++) out[i] = (Math.random() * 2 - 1) * 0.4;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filt = c.createBiquadFilter();
  filt.type = "bandpass";
  filt.frequency.value = filterFreq;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  src.connect(filt).connect(gain).connect(c.destination);
  src.start(t0); src.stop(t0 + duration + 0.05);
}

function sweep(f0, f1, t0, dur, type = "sawtooth", peak = 0.25) {
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(f0, t0);
  osc.frequency.exponentialRampToValueAtTime(Math.max(f1, 1), t0 + dur);
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain).connect(c.destination);
  osc.start(t0); osc.stop(t0 + dur + 0.05);
}

const SFX = {
  applause:    (c, t) => { for (let i = 0; i < 30; i++) noiseBurst(t + i * 0.05, 0.08, 0.12, 2200); },
  airhorn:     (c, t) => { tone(220, t, 0.4, "sawtooth", 0.4); tone(440, t, 0.4, "sawtooth", 0.25); },
  drumroll:    (c, t) => { for (let i = 0; i < 20; i++) noiseBurst(t + i * 0.04, 0.04, 0.2, 200); },
  ding:        (c, t) => { tone(1200, t, 0.3, "sine", 0.35); tone(2400, t, 0.25, "sine", 0.2); },
  boom:        (c, t) => { sweep(120, 40, t, 0.8, "sine", 0.4); noiseBurst(t, 0.4, 0.3, 80); },
  swoosh:      (c, t) => { sweep(2000, 200, t, 0.35, "triangle", 0.25); noiseBurst(t, 0.4, 0.15, 1200); },
  record_scratch: (c, t) => { sweep(800, 200, t, 0.15, "sawtooth", 0.3); sweep(200, 800, t + 0.15, 0.15, "sawtooth", 0.3); },
  cash:        (c, t) => { tone(1800, t, 0.06, "square", 0.3); tone(2200, t + 0.08, 0.06, "square", 0.3); tone(2600, t + 0.16, 0.06, "square", 0.3); noiseBurst(t + 0.3, 0.3, 0.15, 2000); },
  laser:       (c, t) => { sweep(2000, 200, t, 0.4, "sawtooth", 0.3); },
  pop:         (c, t) => { sweep(800, 200, t, 0.08, "sine", 0.4); },
  magic:       (c, t) => { for (let i = 0; i < 10; i++) tone(1200 + Math.random() * 1800, t + i * 0.06, 0.1, "triangle", 0.18); },
  siren:       (c, t) => { for (let i = 0; i < 4; i++) sweep(800, 1600, t + i * 0.4, 0.4, "sine", 0.3); },
};

export function playSfx(id) {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") c.resume().catch(() => {});
  const t = c.currentTime + 0.02;
  const fn = SFX[id];
  if (fn) try { fn(c, t); } catch (e) { /* ignore */ }
}

// -------- Text-to-speech --------
let voices = [];

function loadVoices() {
  if (typeof window === "undefined" || !window.speechSynthesis) return [];
  voices = window.speechSynthesis.getVoices();
  return voices;
}

if (typeof window !== "undefined" && window.speechSynthesis) {
  loadVoices();
  window.speechSynthesis.onvoiceschanged = loadVoices;
}

export function listVoices() {
  if (!voices.length) loadVoices();
  return voices.map((v) => ({ id: v.voiceURI, name: v.name, lang: v.lang, default: v.default }));
}

/** Speak text aloud using the browser's TTS. Optional voiceURI + rate (0.5–2). */
export function speak(text, { voiceURI, rate = 1, pitch = 1 } = {}) {
  if (typeof window === "undefined" || !window.speechSynthesis || !text) return null;
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.rate = rate;
  utter.pitch = pitch;
  if (voiceURI) {
    const v = (voices.length ? voices : loadVoices()).find((x) => x.voiceURI === voiceURI);
    if (v) utter.voice = v;
  }
  window.speechSynthesis.speak(utter);
  return utter;
}

export function stopSpeaking() {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
}
